import {
  HttpErrorResponse, HttpEvent, HttpHandler,
  HttpInterceptor, HttpParams, HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AccessType } from '../../shared/enum/access-type.enum';
import { JwtToken } from '../../shared/models/jwt-token.model';
import { AuthService } from '../auth/auth.service';
import { CaptchaHelperService } from '../captcha-helper/captcha-helper.service';
import { OccTokenHandlerService } from '../occ-token-handler/occ-token-handler.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  authService: AuthService;

  constructor(
    private injector: Injector,
    private captchaHelperService: CaptchaHelperService,
    private tokenService: OccTokenHandlerService) { }

  /**
   *
   *
   * Method responsible for adding token to req before sending them to pointing server.
   *
   * This method also responsible for overriding content type as text(excluding blob type)
   *
   * to avoid angular default json parser error, which comes when we get blank reponse with
   *
   * 200 status code instead 204.
   *
   *
   *
   * @param req
   * @param token
   * @returns
   * @memberof ApiInterceptor
   */
  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    let newparams = new HttpParams({ fromString: req.params.toString() });
    const reqAuthParam = req.params.get('isAuthRequired');
    const isAuthenticationRequired = (reqAuthParam && reqAuthParam.toLowerCase() === 'true') ? true : false;
    newparams = newparams.delete('pointTo');
    newparams = newparams.delete('isAuthRequired');
    newparams = newparams.delete('isCaptchaTokenRequired');
    if (token && isAuthenticationRequired) {
      return req.clone({
        setHeaders: { authorization: 'Bearer ' + token },
        params: newparams
      });
    } else {
      return req.clone({
        params: newparams
      });
    }
  }

  /**
   *
   *
   * Intercept function for RequestInterceptor interface is centralised to handle all the
   *
   * API response and errors before handling at service or component level
   *
   *
   *
   * @param request The outgoing request object for which the exception needs to be handled
   * @param next  The next interceptor in the chain, or the backend
   *  if no interceptors remain in the chain.
   * @returns An observable of the event stream.
   * @memberof ApiInterceptor
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.authService = this.injector.get(AuthService);
    const pointTo = request.params.get('pointTo');
    const ownerJwtToken: JwtToken = this.tokenService.getJwtToken(true);
    const userJwtToken: JwtToken = this.tokenService.getJwtToken(false);
    const isCaptchaTokenRequired = request.params.get('isCaptchaTokenRequired');
    const token = this.authService.getAuthToken(pointTo, isCaptchaTokenRequired);
    return next.handle(this.addToken(request, token))
      .pipe(catchError((error: HttpErrorResponse) => {
        const errorObj = {
          apiUrl: request.url,
          error,
        };
        this.tokenService.logHttpError(JSON.stringify(errorObj));
        if (error.status && error.status === 401) {
          if (this.tokenService.checkAuthorizationToken(token) === AccessType.CAPTCHA_OCC ||
            this.tokenService.checkAuthorizationToken(token) === AccessType.CAPTCHA_MASHERY) {
            return this.handle401Error(request, next);
          } else if (this.tokenService.checkAuthorizationToken(token) === AccessType.USER_OCC ||
            this.tokenService.checkAuthorizationToken(token) === AccessType.USER_MASHERY) {
            return this.handleOcc401Error(request, next, userJwtToken.refreshToken, false);
          } else if (this.tokenService.checkAuthorizationToken(token) === AccessType.OWNER_OCC ||
            this.tokenService.checkAuthorizationToken(token) === AccessType.OWNER_MASHERY) {
            return this.handleOcc401Error(request, next, ownerJwtToken.refreshToken, true);
          } else {
            this.authService.handleTokenExpiry();
            return throwError(error);
          }
        } else {
          return throwError(error);
        }
      }));
  }

  /**
   *
   *
   * Method is responsible for handling 401(unauthorized) response and
   *
   * initiate refresh token flow for failed mashery call. Once we get
   *
   * new token then this method will reexecute failed call with the help
   *
   * of behaviour subject.
   *
   *
   *
   * @param req
   * @param next
   * @returns
   * @memberof ApiInterceptor
   */
  handle401Error(req: HttpRequest<any>, next: HttpHandler) {
    const pointTo = req.params.get('pointTo');
    const isCaptchaTokenRequired = req.params.get('isCaptchaTokenRequired');

    try {
      this.captchaHelperService.reloadCaptcha();
      return this.captchaHelperService.tokensFetched.pipe(
        filter(token => token != null)
        , take(1)
        , switchMap(token => next.handle(this.addToken(req, this.authService.getAuthToken(pointTo, isCaptchaTokenRequired)))));
    } catch (error) {
      this.authService.handleTokenExpiry();
      return throwError(error);
    }
  }

  /**
   *
   *
   * @name handleOcc401Error
   * @description Function for occ token 401 error handling
   * @param req
   * @param next
   * @param refreshToken Refresh token
   * @param isOwnerFlow Boolean flag to denote owner or user flow
   * @return
   * @memberof ApiInterceptor
   */
  handleOcc401Error(req: HttpRequest<any>, next: HttpHandler, refreshToken: string, isOwnerFlow: boolean) {
    const pointTo = req.params.get('pointTo');
    return this.tokenService.fetchAndUpdateJwtToken(refreshToken, isOwnerFlow).pipe(
      filter(token => token != null),
      take(1),
      switchMap(token => next.handle(this.addToken(req, this.authService.getAuthToken(pointTo, 'false')))),
      catchError(error => {
        this.authService.handleTokenExpiry();
        return throwError(error);
      }));

  }
}
